import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

export const JOURNEY_TIMELINE_COLORS = ['#00b14f', '#1677ff', '#722ed1', '#fa8c16', '#eb2f96', '#13c2c2'];
export const MAX_JOURNEY_TIMELINE_DAYS = 7;

export type JourneyCommsTimelineResponse = {
  startDate?: string;
  endDate?: string;
  timezone?: string;
  groups?: { groupID: string; totalComms?: number }[] | null;
  buckets?: { timestamp: string; comms: { groupID: string; count: number }[] | null }[] | null;
};

const getCount = (count: unknown) => (typeof count === 'number' && Number.isFinite(count) && count > 0 ? count : 0);
const getGroupColor = (index: number, count: number) => (count <= JOURNEY_TIMELINE_COLORS.length
  ? JOURNEY_TIMELINE_COLORS[index]
  : `hsl(${Math.round(index * 360 / count)}, 70%, 45%)`);

export type JourneyTimelineTimezoneOption = { code?: string; tz?: string };

const isValidTimezone = (timezone?: string): boolean => {
  if (!timezone) return false;
  try {
    Intl.DateTimeFormat('en-US', { timeZone: timezone }).resolvedOptions();
    return true;
  } catch (_) {
    return false;
  }
};

// The API's response.timezone describes UTC transport, not the country's display zone.
// Prefer the campaign's configured zone within the country, then the country default.
export const resolveJourneyTimelineDisplayTimezone = (
  countryTimezones: JourneyTimelineTimezoneOption[] = [], campaignTimezone?: string,
): string => {
  const timezones = Array.isArray(countryTimezones) ? countryTimezones : [];
  const configured = timezones.find((option) => (
    option.code === campaignTimezone || option.tz === campaignTimezone
  ));
  const candidates = [configured && configured.tz, timezones[0] && timezones[0].tz, campaignTimezone];
  return candidates.find((timezone) => isValidTimezone(timezone)) || 'UTC';
};

const formatBucketLabel = (timestamp: string, displayTimezone: string) => {
  const date = dayjs.utc(timestamp);
  return date.isValid() ? date.tz(displayTimezone).format('DD MMM HH:mm') : timestamp;
};

// Query dates remain inclusive UTC days; show their actual bounds in country-local time.
export const getJourneyTimelineDisplayRange = (startDate: string, endDate: string, displayTimezone: string) => {
  const timezone = resolveJourneyTimelineDisplayTimezone([], displayTimezone);
  return {
    start: dayjs.utc(startDate).tz(timezone).format('DD MMM YYYY HH:mm'),
    end: dayjs.utc(endDate).add(1, 'day').subtract(1, 'minute').tz(timezone).format('DD MMM YYYY HH:mm'),
  };
};

// This is a projection of the real API response; it never generates mock comms.
export const buildJourneyTimelineChart = (
  response: JourneyCommsTimelineResponse, groupIDs: string[], displayTimezone = 'UTC',
) => {
  const timezone = resolveJourneyTimelineDisplayTimezone([], displayTimezone);
  const buckets = Array.isArray(response.buckets) ? response.buckets : [];
  const totals = Array.isArray(response.groups) ? response.groups : [];
  const ids = Array.from(new Set([
    ...groupIDs,
    ...totals.map((group) => group.groupID),
    ...buckets.flatMap((bucket) => (Array.isArray(bucket.comms) ? bucket.comms.map((item) => item.groupID) : [])),
  ])).filter(Boolean);
  const datasets = ids.map((id, index) => ({
    label: id,
    backgroundColor: getGroupColor(index, ids.length),
    borderWidth: 0,
    data: buckets.map((bucket) => (Array.isArray(bucket.comms) ? bucket.comms : [])
      .filter((item) => item.groupID === id).reduce((sum, item) => sum + getCount(item.count), 0)),
  }));
  return {
    labels: buckets.map((bucket) => formatBucketLabel(bucket.timestamp, timezone)),
    datasets,
    totals: datasets.map((dataset) => {
      const declared = totals.find((group) => group.groupID === dataset.label);
      const hasDeclaredTotal = declared && typeof declared.totalComms === 'number'
        && Number.isFinite(declared.totalComms) && declared.totalComms >= 0;
      return {
        groupID: dataset.label,
        color: dataset.backgroundColor,
        total: hasDeclaredTotal ? getCount(declared && declared.totalComms) : dataset.data.reduce((sum, count) => sum + count, 0),
        isRangeSum: !hasDeclaredTotal,
      };
    }),
  };
};

export const getJourneyTimelineRangeError = (start: string, end: string): string | undefined => {
  const startDate = dayjs.utc(start);
  const endDate = dayjs.utc(end);
  if (!start || !end || !startDate.isValid() || !endDate.isValid()) return 'Select a start and end date.';
  const days = endDate.startOf('day').diff(startDate.startOf('day'), 'day');
  if (days < 0) return 'End date must not be earlier than start date.';
  return days >= MAX_JOURNEY_TIMELINE_DAYS ? 'Select a date range of no more than 7 days.' : undefined;
};
